import React from 'react';
import { styles } from "../../../../utils/styles";
import { EyeIconDisable, MultiChoiceIcon, PlaceHolderIconChip } from 'assests/GmsIcons';

type Choice = {
  av_id: string;
  av_value: string;
  display_value: string;
}

type MultiChoiceFieldProps = {
  type?: 'checkbox' | 'chip' | 'radio' | 'dropdown';
  direction?: 'horizontal' | 'vertical';
  choices?: Choice[];
  title?: string;
  label?: string;
  displayName?: string;
  showTitle?: boolean;
  id?: string;
  isSelected?: boolean;
  style?: 'Chip' | 'Checkbox' | 'Radio Button' | 'Dropdown';
  layoutDirection?: 'Horizontal' | 'Vertical';
  showImage?: boolean;
  imageDisplay?: 'Fit' | 'Fill' | 'Stretch';
  chipLabelPosition?: 'Right side of image' | 'Below Image' | 'Show only on hover' | 'Hide label' | 'Left' | 'Centre' | 'Right';
  captionAlignment?: 'Left' | 'Centre' | 'Right';
  chipSize?: 'Compact' | 'Medium' | 'Large';
  layoutStyle?: 'Standard' | 'Padded Frame';
}

const MultiChoiceField = ({
  type = 'radio',
  direction = 'horizontal',
  choices = [],
  title = 'Title',
  label = 'Guide to Select Enclosure',
  displayName,
  showTitle = true,
  id,
  isSelected = false,
  style = 'Chip',
  layoutDirection = 'Horizontal',
  showImage = false,
  imageDisplay = 'Fit',
  chipLabelPosition = 'Below Image',
  captionAlignment = 'Centre',
  chipSize = 'Medium',
  layoutStyle = 'Standard'
}: MultiChoiceFieldProps) => {

  const displayLabel = showTitle && displayName ? displayName : (showTitle ? label : '');
  const baseId = id || 'multichoice-field';

  const actualDirection = layoutDirection?.toLowerCase() as 'horizontal' | 'vertical' || direction;

  return (
    <div id={baseId} data-testid={baseId}>
      <div id={`${baseId}-header`} className='tw-flex tw-items-center tw-justify-start tw-gap-1 tw-mb-2 tw-ps-2'>
        <MultiChoiceIcon />

        {!showTitle && <EyeIconDisable />}

        {!showTitle && (
          <div id={`${baseId}-hidden-title`} className="tw-text-[#172B4D] tw-text-sm tw-font-medium">
            Title
          </div>
        )}

        {showTitle && displayLabel && (
          <div id={`${baseId}-label`} className="tw-ms-1 tw-justify-start tw-text-[#172b4d] tw-text-sm tw-font-medium">
            {displayLabel}
          </div>
        )}
      </div>

      {renderMultiChoiceByStyle(style, actualDirection, choices, showImage, imageDisplay, chipLabelPosition, captionAlignment, chipSize, layoutStyle, baseId)}
    </div>
  );
};

const renderMultiChoiceByStyle = (
    style: 'Chip' | 'Checkbox' | 'Radio Button' | 'Dropdown',
    direction: 'horizontal' | 'vertical',
    choices: Choice[],
    showImage: boolean,
    imageDisplay?: 'Fit' | 'Fill' | 'Stretch',
    chipLabelPosition?: 'Right side of image' | 'Below Image' | 'Show only on hover' | 'Hide label' | 'Left' | 'Centre' | 'Right',
    captionAlignment?: 'Left' | 'Centre' | 'Right',
    chipSize?: 'Compact' | 'Medium' | 'Large',
    layoutStyle?: 'Standard' | 'Padded Frame',
    baseId?: string
  ) => {
    switch (style) {
      case 'Chip':
        return <ChipStyle direction={direction} choices={choices} showImage={showImage} imageDisplay={imageDisplay} chipLabelPosition={chipLabelPosition} captionAlignment={captionAlignment} chipSize={chipSize} layoutStyle={layoutStyle} baseId={baseId} />;
      case 'Checkbox':
        return <CheckboxStyle direction={direction} choices={choices} baseId={baseId} />;
      case 'Radio Button':
        return <RadioStyle direction={direction} choices={choices} baseId={baseId} />;
      case 'Dropdown':
        return <DropdownStyle choices={choices} baseId={baseId} />;
      default:
        return <ChipStyle direction={direction} choices={choices} showImage={showImage} imageDisplay={imageDisplay} chipLabelPosition={chipLabelPosition} captionAlignment={captionAlignment} chipSize={chipSize} layoutStyle={layoutStyle} baseId={baseId} />;
    }
  };

const ChipStyle = ({
  direction,
  choices = [],
  showImage = false,
  imageDisplay = 'Fit',
  chipLabelPosition = 'Right side of image',
  captionAlignment = 'Centre',
  chipSize = 'Medium',
  layoutStyle = 'Standard',
  baseId = 'chip-style'
}: {
  direction: 'horizontal' | 'vertical',
  choices: Choice[],
  showImage?: boolean,
  imageDisplay?: 'Fit' | 'Fill' | 'Stretch',
  chipLabelPosition?: 'Right side of image' | 'Below Image' | 'Show only on hover' | 'Hide label' | 'Left' | 'Centre' | 'Right',
  captionAlignment?: 'Left' | 'Centre' | 'Right',
  chipSize?: 'Compact' | 'Medium' | 'Large',
  layoutStyle?: 'Standard' | 'Padded Frame',
  baseId?: string
}) => {
  const displayChoices = choices;
  const hasChoices = displayChoices && displayChoices.length > 0;

  const isLargeChip = chipSize === 'Large';

  const getChipLayout = (position?: string, alignment?: string) => {
    const chipAlignment = alignment?.toLowerCase() || 'left';

    if (!showImage) {
      return { layout: 'default', alignment: chipAlignment };
    }

    switch (position) {
      case 'Below Image':
        return { layout: 'column', alignment: chipAlignment };
      case 'Show only on hover':
        return { layout: 'hover-column', alignment: chipAlignment };
      case 'Hide label':
        return { layout: 'hidden', alignment: 'left' };
      case 'Right side of image':
      default:
        return { layout: 'row', alignment: chipAlignment };
    }
  };

  const { layout, alignment } = getChipLayout(chipLabelPosition, captionAlignment);

  const getSvgIconScale = (mode?: 'Fit' | 'Fill' | 'Stretch') => {
    switch (mode) {
      case 'Fill':
        return 'tw-scale-125';
      case 'Stretch':
        return 'tw-scale-150';
      case 'Fit':
      default:
        return 'tw-scale-75';
    }
  };

  const renderChip = (choice: Choice | null, index: number, selected: boolean = false) => {
    const chipKey = choice?.av_id || `chip-${index}`;
    const chipValue = choice?.av_value || `Option ${index + 1}`;
    const chipId = `${baseId}-chip-${index}`;

    const imageSize = isLargeChip ? 'tw-w-24 tw-h-24' : 'tw-w-20 tw-h-20';

    const containerPadding = isLargeChip ? 'tw-px-4 tw-py-3' : 'tw-px-3 tw-py-3';

    // Apply different styling based on layoutStyle
    const isPaddedFrame = layoutStyle === 'Padded Frame';
    const borderStyle = 'tw-border tw-border-[#DADBDD]';
    const backgroundStyle = 'tw-bg-white';
    const shadowStyle = isPaddedFrame ? 'tw-shadow-sm' : '';

    const chipContainerClasses = `${borderStyle} ${containerPadding} ${backgroundStyle} ${shadowStyle} ${
      layout === 'hover-column' ? 'tw-group tw-relative' : ''
    } ${isLargeChip ? 'tw-min-w-[140px]' : 'tw-min-w-[120px]'} tw-rounded`;

    // Selection border styling - red outer border when selected
    const selectionWrapperClasses = selected
      ? 'tw-p-1 tw-border-2 tw-border-[#E94B35]'
      : 'tw-p-1';

    const getTextAlignmentClass = () => {
      if (layout !== 'column' && layout !== 'hover-column') return '';
      switch (alignment) {
        case 'left':
          return 'tw-text-left';
        case 'center':
          return 'tw-text-center';
        case 'right':
          return 'tw-text-right';
        default:
          return 'tw-text-center';
      }
    };

    const contentClasses = (layout === 'column' || layout === 'hover-column')
      ? 'tw-flex tw-flex-col tw-items-center tw-gap-3 tw-w-full'
      : 'tw-flex tw-items-center';

    const textClasses = `tw-text-[#172B4D] tw-text-xs tw-font-medium ${getTextAlignmentClass()} ${showImage && layout === 'row' ? 'tw-ml-3' : ''} ${isLargeChip ? 'tw-w-full' : ''} tw-overflow-hidden tw-text-ellipsis tw-whitespace-nowrap`;

    return (
      <div key={chipKey} id={chipId} data-testid={chipId} className={`tw-flex tw-items-center ${selectionWrapperClasses}`}>
        <div id={`${chipId}-container`} className={chipContainerClasses}>
          <div id={`${chipId}-content`} className={contentClasses}>
            {showImage && (
              <div id={`${chipId}-image`} className="tw-flex-shrink-0">
                <div className={`${imageSize} tw-overflow-hidden tw-rounded tw-flex tw-items-center tw-justify-center tw-border tw-border-[#DADBDD]`}>
                  <div className={getSvgIconScale(imageDisplay)}>
                    <PlaceHolderIconChip />
                  </div>
                </div>
              </div>
            )}
            {layout === 'hidden' ? null : layout === 'hover-column' ? (
              <div id={`${chipId}-label`} className={`tw-text-[#172B4D] tw-text-xs ${getTextAlignmentClass()} tw-opacity-0 group-hover:tw-opacity-100 tw-transition-opacity ${isLargeChip ? 'tw-w-full' : ''} tw-overflow-hidden tw-text-ellipsis tw-whitespace-nowrap`} title={chipValue}>
                {chipValue}
              </div>
            ) : (
              <div id={`${chipId}-label`} className={textClasses} title={chipValue}>
                {chipValue}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div id={`${baseId}-chip-container`} className="tw-mb-3 tw-ps-7 tw-pr-6">
      <div
        id={`${baseId}-chip-list`}
        className={`tw-flex ${
          direction === 'vertical'
            ? 'tw-flex-col tw-gap-4'
            : 'tw-gap-6 tw-overflow-x-auto tw-flex-nowrap hide-scrollbar'
        }`}
      >
        {hasChoices
          ? displayChoices.map((choice, index) => renderChip(choice, index))
          : [renderChip(null, 0, true), renderChip(null, 1)]
        }
      </div>
    </div>
  );
};

const CheckboxStyle = ({ direction, choices = [], baseId = 'checkbox-style' }: { direction: 'horizontal' | 'vertical', choices: Choice[], baseId?: string }) => {
  const displayChoices = choices;
  const hasChoices = displayChoices && displayChoices.length > 0;

  return (
    <div id={`${baseId}-checkbox-container`} className="tw-mb-3 tw-ps-7">
      <div id={`${baseId}-checkbox-list`} className={`tw-flex ${direction === 'vertical' ? 'tw-flex-col tw-gap-2' : 'tw-flex-row tw-gap-6'}`}>
        {hasChoices
          ? displayChoices.map((choice, index) => (
              <label key={choice.av_id || `checkbox-${index}`} id={`${baseId}-checkbox-item-${index}`} className="tw-flex tw-items-center">
                <input
                  id={`${baseId}-checkbox-input-${index}`}
                  type="checkbox"
                  className={`${styles.checkboxInput} tw-rounded tw-mr-2`}
                  checked={false}
                  onChange={() => {}}
                />
                <span id={`${baseId}-checkbox-label-${index}`} className="tw-text-[#172B4D] tw-text-xs">{choice.av_value}</span>
              </label>
            ))
          : [
              <label key="checkbox-1" id={`${baseId}-checkbox-item-0`} className="tw-flex tw-items-center">
                <input
                  id={`${baseId}-checkbox-input-0`}
                  type="checkbox"
                  className={`${styles.checkboxInput} tw-h-5 tw-w-5 tw-rounded tw-mr-2`}
                  checked={false}
                  onChange={() => {}}
                />
                <span id={`${baseId}-checkbox-label-0`} className="tw-text-[#172b4d] tw-font-medium">Option 1</span>
              </label>,
              <label key="checkbox-2" id={`${baseId}-checkbox-item-1`} className="tw-flex tw-items-center">
                <input
                  id={`${baseId}-checkbox-input-1`}
                  type="checkbox"
                  className={`${styles.checkboxInput} tw-h-5 tw-w-5 tw-rounded tw-mr-2`}
                  checked={false}
                  onChange={() => {}}
                />
                <span id={`${baseId}-checkbox-label-1`} className="tw-text-[#172b4d] tw-font-medium">Option 2</span>
              </label>
            ]
        }
      </div>
    </div>
  );
};

const RadioStyle = ({ direction, choices = [], baseId = 'radio-style' }: { direction: 'horizontal' | 'vertical', choices: Choice[], baseId?: string }) => {
  const displayChoices = choices;
  const hasChoices = displayChoices && displayChoices.length > 0;

  return (
    <div id={`${baseId}-radio-container`} className="tw-mb-3 tw-ps-7">
      <div id={`${baseId}-radio-list`} className={`tw-flex ${direction === 'vertical' ? 'tw-flex-col tw-gap-2' : 'tw-flex-row tw-gap-6'}`}>
        {hasChoices
          ? displayChoices.map((choice, index) => (
              <label key={choice.av_id || `radio-${index}`} id={`${baseId}-radio-item-${index}`} className="tw-flex tw-items-center">
                <input
                  id={`${baseId}-radio-input-${index}`}
                  type="radio"
                  name={`${baseId}-radio-group`}
                  className="tw-rounded-full tw-border-2 tw-border-[#6D788C] tw-mr-2"
                  checked={false}
                  onChange={() => {}}
                />
                <span id={`${baseId}-radio-label-${index}`} className="tw-text-[#172b4d] tw-text-xs">{choice.av_value}</span>
              </label>
            ))
          : [
              <label key="radio-1" id={`${baseId}-radio-item-0`} className="tw-flex tw-items-center">
                <input
                  id={`${baseId}-radio-input-0`}
                  type="radio"
                  name={`${baseId}-radio-group`}
                  className="tw-h-5 tw-w-5 tw-rounded-full tw-border-2 tw-border-gray-300 tw-mr-2"
                  checked={false}
                  onChange={() => {}}
                />
                <span id={`${baseId}-radio-label-0`} className="tw-text-[#172b4d] tw-font-medium">Option 1</span>
              </label>,
              <label key="radio-2" id={`${baseId}-radio-item-1`} className="tw-flex tw-items-center">
                <input
                  id={`${baseId}-radio-input-1`}
                  type="radio"
                  name={`${baseId}-radio-group`}
                  className="tw-h-5 tw-w-5 tw-rounded-full tw-border-2 tw-border-gray-300 tw-mr-2"
                  checked={false}
                  onChange={() => {}}
                />
                <span id={`${baseId}-radio-label-1`} className="tw-text-[#172b4d] tw-font-medium">Option 2</span>
              </label>
            ]
        }
      </div>
    </div>
  );
};

const DropdownStyle = ({ choices = [], baseId = 'dropdown-style' }: { choices: Choice[], baseId?: string }) => {
  return (
    <div id={`${baseId}-dropdown-container`} className="tw-mb-3 tw-ps-7">
      <div id={`${baseId}-dropdown-wrapper`} className="tw-relative" style={{ width: '130px' }}>
        <select
          id={`${baseId}-dropdown-select`}
          value={''}
          onChange={() => {}}
          className="tw-appearance-none tw-block tw-w-full tw-px-3 tw-py-2 tw-border tw-border-[#6D788C] tw-rounded-[4px] tw-bg-transparent tw-text-[#6D788C] tw-leading-tight focus:tw-outline-none focus:tw-bg-white focus:tw-border-blue-500"
        >
          <option id={`${baseId}-dropdown-placeholder`} value="">Select</option>
          {choices.length > 0
            ? choices.map((choice, index) => (
                <option key={choice.av_id} id={`${baseId}-dropdown-option-${index}`} value={choice.av_value}>
                  {choice.av_value}
                </option>
              ))
            : [
                <option key="option-1" id={`${baseId}-dropdown-option-0`} value="option-1">Option 1</option>,
                <option key="option-2" id={`${baseId}-dropdown-option-1`} value="option-2">Option 2</option>
              ]
          }
        </select>
        <div id={`${baseId}-dropdown-icon`} className="tw-pointer-events-none tw-absolute tw-inset-y-0 tw-right-0 tw-flex tw-items-center tw-px-2 tw-text-[#6D788C]">
          <svg className="tw-fill-current tw-h-4 tw-w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
            <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z"/>
          </svg>
        </div>
      </div>
    </div>
  );
};

export default MultiChoiceField;